import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyFormModal = ({ isOpen, onClose, company = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    legal_name: '',
    trading_name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    phone: '',
    fax: '',
    email: '',
    website: '',
    registration_number: '',
    vat_number: '',
    trn: '',
    license_number: '',
    logo_base64: '',
    theme_color: '#023E8A',
    tax_id_label: 'TRN',
    is_default: false,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');
  const isEdit = !!company;

  useEffect(() => {
    if (company) {
      setFormData({
        legal_name: company.legal_name || '',
        trading_name: company.trading_name || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        country: company.country || '',
        postal_code: company.postal_code || '',
        phone: company.phone || '',
        fax: company.fax || '',
        email: company.email || '',
        website: company.website || '',
        registration_number: company.registration_number || '',
        vat_number: company.vat_number || '',
        trn: company.trn || '',
        license_number: company.license_number || '',
        logo_base64: company.logo_base64 || '',
        theme_color: company.theme_color || '#023E8A',
        tax_id_label: company.tax_id_label || 'TRN',
        is_default: company.is_default || false,
        is_active: company.is_active !== false
      });
    }
  }, [company]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Logo file size must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo_base64: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') {
          delete payload[key];
        }
      });

      const url = isEdit
        ? `${API_URL}/api/procurement/companies/${company.id}`
        : `${API_URL}/api/procurement/companies`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save company');
      }

      toast({
        title: 'Success',
        description: `Company ${isEdit ? 'updated' : 'created'} successfully`
      });

      onSuccess();
    } catch (error) {
      console.error('Error saving company:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#2B2D42]">
            {isEdit ? 'Edit Company' : 'Create New Company'}
          </h2>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#2B2D42]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Logo Upload + Brand Colour */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Company Logo</label>
              <div className="flex items-center space-x-4">
                {formData.logo_base64 && (
                  <img
                    src={formData.logo_base64}
                    alt="Logo preview"
                    className="h-20 w-20 object-contain rounded-lg border border-gray-300 p-2 bg-white"
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="text-sm text-[#6C757D]"
                />
              </div>
              <p className="text-xs text-[#6C757D] mt-1">Max 5MB. PNG, JPG, SVG recommended.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Brand Colour</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  name="theme_color"
                  value={formData.theme_color || '#023E8A'}
                  onChange={handleChange}
                  className="w-14 h-10 rounded border border-gray-300 cursor-pointer"
                  data-testid="company-theme-color-picker"
                />
                <input
                  type="text"
                  name="theme_color"
                  value={formData.theme_color || ''}
                  onChange={handleChange}
                  placeholder="#023E8A"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                />
              </div>
              <p className="text-xs text-[#6C757D] mt-1">Used on invoices, POs &amp; branded docs.</p>
            </div>
          </div>

          {/* Legal Name & Trading Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Legal Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="legal_name"
                value={formData.legal_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Trading Name</label>
              <input
                type="text"
                name="trading_name"
                value={formData.trading_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
            />
          </div>

          {/* City, State, Country, Postal */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">State/Province</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Country <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Postal Code</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </div>

          {/* Phone, Fax, Email */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Fax</label>
              <input
                type="tel"
                name="fax"
                value={formData.fax}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </div>

          {/* Website, Registration Number, VAT */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Registration Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="registration_number"
                value={formData.registration_number}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">VAT Number</label>
              <input
                type="text"
                name="vat_number"
                value={formData.vat_number}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </div>

          {/* Tax ID label customization + TRN + License */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Tax ID Label
                <span className="text-xs text-gray-500 ml-1 font-normal">(displayed on PDFs)</span>
              </label>
              <select
                name="tax_id_label"
                value={formData.tax_id_label || 'TRN'}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                data-testid="company-tax-label-select"
              >
                <option value="TRN">TRN (UAE)</option>
                <option value="NIF">NIF (Portugal / Angola)</option>
                <option value="CNPJ">CNPJ (Brazil)</option>
                <option value="VAT">VAT Number</option>
                <option value="EIN">EIN (USA)</option>
                <option value="Tax ID">Tax ID (generic)</option>
                <option value="GSTIN">GSTIN (India)</option>
                <option value="SIRET">SIRET (France)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Used as the label on invoices, POs and official docs.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                {formData.tax_id_label || 'TRN'} Value
              </label>
              <input
                type="text"
                name="trn"
                value={formData.trn}
                onChange={handleChange}
                placeholder="Enter number"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
                data-testid="company-tax-value-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">License Number</label>
              <input
                type="text"
                name="license_number"
                value={formData.license_number || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="is_default"
                checked={formData.is_default}
                onChange={handleChange}
                className="h-4 w-4 text-[#00B4D8] border-gray-300 rounded focus:ring-[#00B4D8]"
              />
              <span className="text-sm text-[#2B2D42]">Set as default company</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-[#00B4D8] border-gray-300 rounded focus:ring-[#00B4D8]"
              />
              <span className="text-sm text-[#2B2D42]">Active</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button type="button" onClick={onClose} variant="outline" disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#00B4D8] hover:bg-[#0096B8]"
              disabled={loading}
            >
              {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Company' : 'Create Company')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyFormModal;